import React, { useEffect, useMemo, useReducer, useState } from "react";
import { useCompanies } from "@/hooks";
import { FilterCompaniesStateContext, YEAR_MIN, YEAR_MAX } from "./contexts";
import { CompanyFiltersState } from "./contexts/FilterCompaniesContext";
import { defaultCompaniesFilters } from "@/hooks/companies/useCompanies";

export type FilterCompaniesAction =
  | { type: "SET_FILTERS"; payload: Partial<CompanyFiltersState> }
  | { type: "RESET_FILTERS" };

function filterCompaniesReducer(
  state: CompanyFiltersState,
  action: FilterCompaniesAction,
): CompanyFiltersState {
  switch (action.type) {
    case "SET_FILTERS":
      return { ...state, ...action.payload };
    case "RESET_FILTERS":
      return defaultCompaniesFilters;
    default:
      return state;
  }
}

export function FilterCompaniesStateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  /* --------------------- Hooks --------------------- */
  const [page, setPage] = useState(1);

  const [filtersState, dispatch] = useReducer(
    filterCompaniesReducer,
    defaultCompaniesFilters,
  );

  const filters = useMemo(() => {
    return { ...filtersState } satisfies CompanyFiltersState;
  }, [filtersState]);

  const skipFetch = Object.values(filters).every(value => {
    if (Array.isArray(value)) return value.length === 0;
    return value === undefined || value === "";
  });

  const {
    companyFilters: filtersReturn,
    companies,
    loading: companiesLoading,
    error: companiesError,
    pagination,
  } = useCompanies({
    skip: skipFetch,
    filters,
    page,
  });

  /* --------------------- Handles --------------------- */
  function resetFilters() {
    dispatch({ type: "RESET_FILTERS" });
    setPage(1);
  }

  /* --------------------- Lifecycle Methods --------------------- */
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filtersState]);

  const value = useMemo(() => {
    const getNoResultsMessage = () => {
      const selectedFilters = [];

      if (filters.selectedCompanies?.length > 0) {
        selectedFilters.push(
          `for companies ${filters.selectedCompanies.map(c => c.company_name).join(", ")}`,
        );
      }

      if (filters.selectedHealthcareDomains?.length > 0) {
        selectedFilters.push(
          `in domains ${filters.selectedHealthcareDomains.map(d => d.value).join(", ")}`,
        );
      }

      if (filters.selectedCompanySizes?.length > 0) {
        selectedFilters.push(
          `for company size ${filters.selectedCompanySizes.map(s => s.size_range).join(", ")}`,
        );
      }

      if (filters.selectedLocations?.length > 0) {
        selectedFilters.push(
          `in locations ${filters.selectedLocations.map(l => l.name).join(", ")}`,
        );
      }

      if (filters.selectedCompanyTypes?.length > 0) {
        selectedFilters.push(
          `for company type ${filters.selectedCompanyTypes.map(t => t.company_type_name).join(", ")}`,
        );
      }

      if (filters.selectedFundingTypes?.length > 0) {
        selectedFilters.push(
          `for funding type ${filters.selectedFundingTypes.map(f => f.funding_type_name).join(", ")}`,
        );
      }

      if (filters.selectedOperatingStatus?.length > 0) {
        selectedFilters.push(
          `for operating status ${filters.selectedOperatingStatus.join(", ")}`,
        );
      }
      if (
        filters.selectedYearRangeMin &&
        filters.selectedYearRangeMax &&
        (filters.selectedYearRangeMin !== YEAR_MIN ||
          filters.selectedYearRangeMax !== YEAR_MAX)
      ) {
        selectedFilters.push(
          `with year founded range between ${filters.selectedYearRangeMin} and ${filters.selectedYearRangeMax}`,
        );
      }

      if (filters.search) {
        selectedFilters.push(`with search query ${filters.search}`);
      }

      let message = "No matching companies";
      if (companies?.length > 0) {
        message += ` ${selectedFilters.join(", ")}.`;
      }
      return message;
    };

    return {
      companies,
      filters,

      loading: companiesLoading,
      error: companiesError,
      dispatch,

      resetFilters,
      noMatchingResults: filtersReturn && companies?.length === 0,
      getNoResultsMessage,

      page,
      setPage,
      pagination,
    };
  }, [
    filters,
    dispatch,
    companies,
    companiesLoading,
    companiesError,
    filtersReturn,
  ]);

  return (
    <FilterCompaniesStateContext.Provider value={value}>
      {children}
    </FilterCompaniesStateContext.Provider>
  );
}
